import React, { useState, useContext, useEffect } from 'react';
import { AppointmentsRowsFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight, useActiveFeature } from 'services/permissions/permissionChecks';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';
import { formatDecimalToHoursAndMinutes } from 'services/utils/convertDecimalToHoursAndMinutes';
import { convertRowsToLocalDateTimes, sortRowsByDate } from 'services/utils/sortDates';
import { combineDateAndTimeInputsToDatetimeString } from 'services/utils/dateTimeUtils';
import { formatRowDurationString, formatBreakDurationString } from 'services/utils/rowDuration';
import { convertHoursAndMinutesToDecimal } from 'services/utils/convertHoursToDecimal';
import { convertLocalDateTimeToUTC, extractDateFromLocalDateTime, extractTimeFromLocalDateTime } from 'internationalization/timezoneConversions';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { AppointmentType, createNewAppointment, Job } from '../../../views/jobs/JobTypes';
import { EquipmentType } from '../../../views/scheduling/SchedulingTypes';
import { UserType } from '../../../views/users/UserTypes';
import { useWorkerPairs, buildAssigneeOptionsWithPairs, findWorkerPairByOptionValue, ForemanSelect, AssigneeOptionType } from '../../scheduling/workerPairUtils';
import DateInput from '../basefields/DateInput';
import TimeInput from '../basefields/TimeInput';
import MultiSelect from '../basefields/MultiSelect';
import Dropdown from '../basefields/Dropdown';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const AppointmentsRowsField: React.FC<AppointmentsRowsFieldType & { job: Job }> = ({
    name, job, dropdownData, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const { errorMessages } = useGlobalContext();
    const { userLocale, userTimezone, getTimeFormat, environmentSettings, userPreferences } = useSettings();
    // const { initializeModal } = useModal();
    const hasRightCheck = useAllowedRight;
    const is12HourFormat = getTimeFormat() === '12h';

    // Build the rows and end date visibility from the job data
    const buildRowsFromJob = (): { rows: AppointmentType[], showEndDateField: boolean } => {
        if (job.appointments && job.appointments.length > 0) {
            // Convert the backend utc datetimes to local datetimes
            const { rowsWithLocalDatetimes } = convertRowsToLocalDateTimes<AppointmentType>(job.appointments, 'start_datetime', 'end_datetime', userLocale, userTimezone)

            // Initiate variable to show end date field
            let shouldShowEndDateField = false;

            // Convert start- and end-datetimes into separated date and time fields
            const processedRows = rowsWithLocalDatetimes.map((row: AppointmentType) => {
                let startDate, startTime, endDate, endTime, utcStartDatetime, utcEndDatetime;

                if (row.start_datetime) {
                    startDate = extractDateFromLocalDateTime(row.start_datetime) || null;
                    startTime = extractTimeFromLocalDateTime(row.start_datetime, userLocale, is12HourFormat) || null;
                    utcStartDatetime = convertLocalDateTimeToUTC(row.start_datetime, userLocale, userTimezone) || null;
                }

                if (row.end_datetime) {
                    endDate = extractDateFromLocalDateTime(row.end_datetime) || null;
                    endTime = extractTimeFromLocalDateTime(row.end_datetime, userLocale, is12HourFormat) || null;
                    utcEndDatetime = convertLocalDateTimeToUTC(row.end_datetime, userLocale, userTimezone) || null;
                }

                // Check if the end_date differs from the start date of non-deleted rows
                if (startDate && endDate && startDate !== endDate && !row.deleted) {
                    shouldShowEndDateField = true;
                }

                // Map the ids of the assignees to an array of ids
                const assigneeIds = row.assignees?.map(assignee => assignee.id) || [];

                // Map the ids of the equipment to an array of ids
                const equipmentIds = row.equipment?.map(equipment => equipment.id) || [];

                // Update the row with the dates and times
                return {
                    ...row,
                    start_datetime: utcStartDatetime || null,
                    end_datetime: utcEndDatetime || null,
                    start_date: startDate || null,
                    start_time: startTime || null,
                    end_date: endDate || null,
                    end_time: endTime || null,
                    assignee_ids: assigneeIds,
                    equipment_ids: equipmentIds,
                    break_duration_string: formatBreakDurationString(row.break_duration),
                };
            });

            // Sort the rows on date
            const { sortedRows } = sortRowsByDate(processedRows, 'start_date');

            // Format the duration
            sortedRows.map((row: AppointmentType) => {

                // If a duration exists, convert it to a time field
                if (row.duration !== null) {

                    // Format the duration decimal value into a string
                    row.duration_string = formatDecimalToHoursAndMinutes(row.duration)

                } else {
                    // If no duration exists, set the duration string to an empty string
                    row.duration_string = '';
                }

                return { ...row };
            });

            return { rows: sortedRows, showEndDateField: shouldShowEndDateField };
        }

        // If the response is empty, create an empty row
        return { rows: [createNewAppointment(job, uuidv4())], showEndDateField: false };
    };

    // Initialize the states directly to prevent an empty first paint of the field
    const [initialRowsState] = useState(() => buildRowsFromJob());
    const [rows, setRows] = useState<AppointmentType[]>(initialRowsState.rows);
    const [assignees, setAssignees] = useState<UserType[]>(dropdownData?.user?.results || []);
    const [equipmentOptions, setEquipmentOptions] = useState<EquipmentType[]>(dropdownData?.equipment?.results || []);
    const equipmentSchedulingActive = useActiveFeature('equipment_scheduling');
    const [appointmentStatuses] = useState<{ value: string, name: string }[]>([ 
        { value: 'to_be_assigned', name: 'appointment.status.to_be_assigned' }, 
        { value: 'reserved', name: 'appointment.status.reserved' }, 
        { value: 'scheduled', name: 'appointment.status.scheduled' }, 
        { value: 'incomplete', name: 'appointment.status.incomplete' }, 
        { value: 'done', name: 'appointment.status.done' }]);
    const [appointmentColors] = useState<{ value: string, name: string, color: string }[]>([ 
        { value: "soft-dark-blue", name: "soft-dark-blue", color: "soft-dark-blue" },
        { value: "soft-blue", name: "soft-blue", color: "soft-blue" },
        { value: "soft-dark-green", name: "soft-dark-green", color: "soft-dark-green" },
        { value: "soft-green", name: "soft-green", color: "soft-green" },
        { value: "soft-yellow", name: "soft-yellow", color: "soft-yellow" },
        { value: "soft-orange", name: "soft-orange", color: "soft-orange" },
        { value: "soft-red", name: "soft-red", color: "soft-red" },
        { value: "soft-gray", name: "soft-gray", color: "soft-gray" },
        { value: "soft-dark-gray", name: "soft-dark-gray", color: "soft-dark-gray" },
        { value: "soft-purple", name: "soft-purple", color: "soft-purple" }]);
    const [showEndDateField, setShowEndDateField] = useState<boolean>(initialRowsState.showEndDateField);
    const [showColorSelection] = useState<boolean>(userPreferences.scheduling_color_mode === 'custom' ? true : false);
    const [scheduleMultipleAppointments, setScheduleMultipleAppointments] = useState<boolean>(environmentSettings.job_appointment_method === 'multiple');
    const [userHasOnlyViewRights] = useState<boolean>(hasRightCheck('only_view'));
    const { workerPairsActive, workerPairs } = useWorkerPairs();

    // Build the assignee options with the worker pairs grouped on top
    const { options: assigneeOptions, groupOptions: assigneeGroupOptions } = buildAssigneeOptionsWithPairs(
        assignees,
        workerPairsActive ? workerPairs : [],
        t('scheduling.worker_pairs.pairs_group_label'),
        t('scheduling.worker_pairs.employees_group_label')
    );

    // Load the appointments from the fetched data
    useEffect(() => {
        const { rows: processedRows, showEndDateField: shouldShowEndDateField } = buildRowsFromJob();
        setShowEndDateField(shouldShowEndDateField);
        setRows(processedRows);
    }, [job]);

    // Set the assignees and the equipment from the dropdown data
    useEffect(() => {
        setAssignees(dropdownData?.user?.results || []);
        setEquipmentOptions(dropdownData?.equipment?.results || []);
    }, [dropdownData]);

    // Set the schedule multiple appointments setting to show/hide the add appointments button
    useEffect(() => {
        if (environmentSettings.job_appointment_method === 'multiple') {
            setScheduleMultipleAppointments(true);
        } else {
            setScheduleMultipleAppointments(false);
        }
    }, [environmentSettings]);

    // Handle the date change
    const handleDateChange = (selectedDate: string | null, fieldName: 'start_date' | 'end_date', identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Get the row data
            const row = updatedRows[rowIndex];

            // Combine the selected date and time values into datetime fields
            let combinedDatetime, utcDatetime;
            if (selectedDate !== null) {

                // Handle datetime conversion when start date field is changed
                if (fieldName === 'start_date') {

                    // If a start time exists, combine the start datetime
                    if (row.start_time) {
                        // Combine the date input and time input into a local datetime string
                        combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, row.start_time);

                        // Convert the local datetime into an utc datetime string for the backend
                        utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                        // Update the start datetime
                        updatedRows[rowIndex].start_datetime = utcDatetime;
                    }
                    
                    // If the start date and end date are equal, and end time exists, combine the start datetime
                    if ((row.end_date === null || (row.start_date === row.end_date)) && row.end_time) {

                        // Set the end date to the selected date
                        updatedRows[rowIndex].end_date = selectedDate;

                        // Combine the date input and time input into a local datetime string
                        combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, row.end_time);

                        // Convert the local datetime into an utc datetime string for the backend
                        utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                        // Update the end datetime
                        updatedRows[rowIndex].end_datetime = utcDatetime;
                    }
                }

                // Handle datetime conversion when end date field is changed
                if (fieldName === 'end_date' && row.end_time) {
                    // Combine the date input and time input into a local datetime string
                    combinedDatetime = combineDateAndTimeInputsToDatetimeString(selectedDate, row.end_time);

                    // Convert the local datetime into an utc datetime string for the backend
                    utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                    // Update the end datetime
                    updatedRows[rowIndex].end_datetime = utcDatetime;
                }
            } else {
                // Handle in case the start date is removed
                if (fieldName === 'start_date') {
                    // If the start date and end date are equal, remove both start- and end-datetimes
                    if (row.start_date && row.end_date && (row.start_date === row.end_date)) {
                        updatedRows[rowIndex].start_datetime = null;
                        updatedRows[rowIndex].end_datetime = null;

                    // Otherwise only remove the start-datetime
                    } else {
                        updatedRows[rowIndex].start_datetime = null;
                    }
                }

                // Handle in case the end date is removed
                if (fieldName === 'end_date') {
                    // If the start date still exists and end time, set the end date to the start date
                    if (row.start_date && row.end_time) {
                        // Combine the date input and time input into a local datetime string
                        combinedDatetime = combineDateAndTimeInputsToDatetimeString(row.start_date, row.end_time);

                        // Convert the local datetime into an utc datetime string for the backend
                        utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                        // Update the end datetime
                        updatedRows[rowIndex].end_datetime = utcDatetime;

                    // Otherwise remove the end-datetime
                    } else {
                        updatedRows[rowIndex].end_datetime = null;
                    }
                }
            }

            // Set the selected value as 
            updatedRows[rowIndex][fieldName] = selectedDate;

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    }

    // Handle time change
    const handleTimeChange = (selectedTime: string | null, fieldName: 'start_time' | 'end_time', identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the selected value as 
            updatedRows[rowIndex][fieldName] = selectedTime;

            // Get the row data
            const row = updatedRows[rowIndex];

            // Combine the time value to a datetime string if the start_date value exists
            let combinedDatetime, utcDatetime;
            if (selectedTime !== null && selectedTime !== '') {

                // Check if the start date exists
                if (row.start_date !== null) {

                    // Handle if the end time changed and the end date differs from the start date
                    if (row.end_date !== null && (row.start_date !== row.end_date) && fieldName === 'end_time') {

                        // Combine the date input and time input into a local datetime string
                        combinedDatetime = combineDateAndTimeInputsToDatetimeString(row.end_date, selectedTime);

                        // Convert the local datetime into an utc datetime string for the backend
                        utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                        // Update the end datetime
                        updatedRows[rowIndex].end_datetime = utcDatetime;
                    }

                    // Handle if both the end- or start-time changed, but the start- and end-dates are equal
                    if ((row.end_date !== null && (row.start_date === row.end_date)) || row.end_date === null) {

                        // Combine the date input and time input into a local datetime string
                        combinedDatetime = combineDateAndTimeInputsToDatetimeString(row.start_date, selectedTime);

                        // Convert the local datetime into an utc datetime string for the backend
                        utcDatetime = convertLocalDateTimeToUTC(combinedDatetime, userLocale, userTimezone);

                        // Update the start datetime
                        if (fieldName === 'start_time') {
                            updatedRows[rowIndex].start_datetime = utcDatetime;
                        }
            
                        // Update the end datetime
                        if (fieldName === 'end_time') {
                            updatedRows[rowIndex].end_datetime = utcDatetime;
                        }    
                    }
                }
            
            } else {
                // If the selected time is set to null, remove the start- or end-datetimes
                if (fieldName === 'start_time') {
                    updatedRows[rowIndex].start_datetime = null;
                }
    
                // Update the end datetime
                if (fieldName === 'end_time') {
                    updatedRows[rowIndex].end_datetime = null;
                }
            }

            // Update the duration after changing the time, minus the break of the row
            const newDurationString = formatRowDurationString(updatedRows[rowIndex]);
            if (newDurationString !== null) {
                updatedRows[rowIndex].duration_string = newDurationString;
            }
            
            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Handle the dropdown change
    const handleDropdownChange = (selectedValue: string, fieldName: 'status' | 'custom_color', identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the selected value as 
            updatedRows[rowIndex][fieldName] = selectedValue

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Check the subcontractor day passes before adding a subcontractor to a row
    const { checkDayPasses } = useSubcontractorDayPassCheck();
    const checkSubcontractorAssignees = async (row: AppointmentType, userIds: number[]): Promise<boolean> => {
        const subcontractorIds = userIds.filter(userId => assignees.find(assignee => assignee.id === userId)?.is_subcontractor_role);
        if (subcontractorIds.length === 0 || !row.start_datetime) return true;
        return checkDayPasses(subcontractorIds.map(userId => ({
            user_id: userId,
            start_datetime: row.start_datetime,
            end_datetime: row.end_datetime,
            timezone: userTimezone
        })));
    };

    // Handle the multiselect change
    const handleMultiSelectChange = async (selectedValue: string, fieldName: 'assignees', identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            let updatedAssignees = rows[rowIndex][fieldName] || [];

            // Check if the selected value is a worker pair pseudo option
            const selectedPair = findWorkerPairByOptionValue(workerPairs, selectedValue);

            // Determine the users which are added by this change
            const currentAssigneeIds = updatedAssignees.map(assignee => assignee.id);
            const addedUserIds = selectedPair
                ? [selectedPair.foreman.id, selectedPair.helper.id].filter(userId => !currentAssigneeIds.includes(userId))
                : (currentAssigneeIds.some(userId => userId.toString() === selectedValue) ? [] : [parseInt(selectedValue, 10)]);

            // Warn and stop when an added subcontractor has no day pass for the planned days
            if (addedUserIds.length > 0 && !(await checkSubcontractorAssignees(updatedRows[rowIndex], addedUserIds))) return;

            if (selectedPair) {
                // Add both pair members as assignee when they are not selected yet
                [selectedPair.foreman.id, selectedPair.helper.id].forEach((userId) => {
                    if (!updatedAssignees.some(assignee => assignee.id === userId)) {
                        const assigneeToAdd = assignees.find(assignee => assignee.id === userId);
                        if (assigneeToAdd) {
                            updatedAssignees.push(assigneeToAdd);
                        }
                    }
                });

                // Mark the foreman of the pair as the foreman of the appointment
                // unless another foreman is already set, so the foreman of the
                // first selected pair stays when a second pair is added
                if (!updatedRows[rowIndex].foreman_id) {
                    updatedRows[rowIndex].foreman_id = selectedPair.foreman.id;
                }
            } else {
                // Get the index of the assignee in the current list
                const assigneeIndex = updatedAssignees.findIndex(assignee => assignee.id.toString() === selectedValue);

                if (assigneeIndex > -1) {
                    // Delete the assignee if it already exists
                    updatedAssignees.splice(assigneeIndex, 1);
                } else {
                    // Add the assignee if it doesn't exist
                    const assigneeToAdd = assignees.find(assignee => assignee.id.toString() === selectedValue);
                    if (assigneeToAdd) {
                        updatedAssignees.push(assigneeToAdd);
                    }
                }
            }

            // Clear the foreman when they are no longer one of the assignees
            if (updatedRows[rowIndex].foreman_id && !updatedAssignees.some(assignee => assignee.id === updatedRows[rowIndex].foreman_id)) {
                updatedRows[rowIndex].foreman_id = null;
            }

            // Update assignees for the specific row
            updatedRows[rowIndex][fieldName] = updatedAssignees;
            updatedRows[rowIndex]['assignee_ids'] = updatedAssignees.map(assignee => assignee.id);

            // Update the status based on whether there are assignees or equipment
            updatedRows[rowIndex].status = deriveRowStatus(updatedAssignees.length, updatedRows[rowIndex].equipment?.length || 0);

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Determine the status of a row from its number of assignees and equipment
    const deriveRowStatus = (assigneeCount: number, equipmentCount: number): string => {
        return assigneeCount === 0 && equipmentCount === 0 ? 'to_be_assigned' : 'scheduled';
    };

    // Handle the break change of a row and recalculate its duration
    const handleBreakChange = (selectedTime: string | null, identifier: string) => {
        setRows(currentRows => {
            const updatedRows = [...currentRows];
            const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);
            if (rowIndex === -1) return currentRows;

            // Convert the break time string into decimal hours for the backend
            const breakDuration = selectedTime ? convertHoursAndMinutesToDecimal(selectedTime) : null;
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                break_duration_string: selectedTime,
                break_duration: breakDuration !== null && !isNaN(breakDuration) ? breakDuration : null,
            };

            // Recalculate the duration with the new break
            const newDurationString = formatRowDurationString(updatedRows[rowIndex]);
            if (newDurationString !== null) {
                updatedRows[rowIndex].duration_string = newDurationString;
            }

            // Update the updated data with the new rows
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))

            return updatedRows;
        });
    };

    // Handle the equipment multiselect change
    const handleEquipmentChange = (selectedValue: string, identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            const updatedEquipment = [...(rows[rowIndex].equipment || [])];

            // Toggle the selected equipment in the current list
            const equipmentIndex = updatedEquipment.findIndex(equipment => equipment.id.toString() === selectedValue);
            if (equipmentIndex > -1) {
                updatedEquipment.splice(equipmentIndex, 1);
            } else {
                const equipmentToAdd = equipmentOptions.find(equipment => equipment.id.toString() === selectedValue);
                if (equipmentToAdd) {
                    updatedEquipment.push(equipmentToAdd);
                }
            }

            // Update the equipment for the specific row
            updatedRows[rowIndex].equipment = updatedEquipment;
            updatedRows[rowIndex].equipment_ids = updatedEquipment.map(equipment => equipment.id);

            // Update the status based on whether there are assignees or equipment
            updatedRows[rowIndex].status = deriveRowStatus(updatedRows[rowIndex].assignees?.length || 0, updatedEquipment.length);

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Handle the foreman change of a row
    const handleForemanChange = (foremanId: number | null, identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the foreman of the row
            updatedRows[rowIndex].foreman_id = foremanId;

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Handle the lock toggle of a row
    const handleLockToggle = (identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Toggle the locked state of the row
            updatedRows[rowIndex].locked = !updatedRows[rowIndex].locked;

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Handle the creation of a new row
    const handleAddRow = () => {
        // Create a new row
        const newRow = createNewAppointment(job, uuidv4());

        // Get the last non deleted row to prefill the new row
        const lastRow = rows.filter(row => !row.deleted).slice(-1)[0];

        // Prefill the new row with the next date and the times of the last row
        if (lastRow && lastRow.start_date) {

            // Set the start date of the new row to the day after the last row
            const nextDate = new Date(lastRow.start_date);
            nextDate.setDate(nextDate.getDate() + 1);
            const nextDateString = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
            newRow.start_date = nextDateString;

            // Copy the times and duration of the last row
            newRow.start_time = lastRow.start_time;
            newRow.end_time = lastRow.end_time;
            newRow.duration_string = lastRow.duration_string;
            newRow.duration = lastRow.duration;
            newRow.break_duration = lastRow.break_duration;
            newRow.break_duration_string = lastRow.break_duration_string;

            // Combine the date and times into the backend datetime fields
            if (lastRow.start_time) {
                const combinedStartDatetime = combineDateAndTimeInputsToDatetimeString(nextDateString, lastRow.start_time);
                newRow.start_datetime = convertLocalDateTimeToUTC(combinedStartDatetime, userLocale, userTimezone);
            }
            if (lastRow.end_time) {
                const combinedEndDatetime = combineDateAndTimeInputsToDatetimeString(nextDateString, lastRow.end_time);
                newRow.end_datetime = convertLocalDateTimeToUTC(combinedEndDatetime, userLocale, userTimezone);
            }
        }

        // Add the row to the list of rows
        const updatedRows = [...rows, newRow];

        // Update the rows and updated data
        setRows(updatedRows);
        setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
    };

    // TODO in a later stage. Add new appointments by the schedule job modal. Handle the opening of the schedule job modal
    // const handleOpenModal = (event: React.MouseEvent) => {
    //     if (isEditable && !userHasOnlyViewRights) {
    //         // Only open modal when the object is editable
    //         event.preventDefault()
    //         initializeModal(
    //             <ScheduleJobModal jobId={parseInt(itemId)} setLoading={isLoading => setShowLoader(isLoading)} />, { modalSize: 'medium' }
    //         );
    //     }
    // };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        let updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Get the row from the updated rows
            const row = updatedRows[rowIndex]

            // Check if the row is a new row added in the frontend (it has an uuid but no id)
            if (!row.id && row.uuid) {
                // Directly remove it from the updated rows
                updatedRows = updatedRows.filter((_, index) => index !== rowIndex)
        
            } else {
                // Flag the backend known row as deleted without actually delete it
                updatedRows[rowIndex].deleted = true;
            }
    
            // Update the ordering of the items which are not marked as deleted
            const nonDeletedItems = updatedRows.filter(row => !row.deleted);
            const reorderedNonDeletedItems = nonDeletedItems.map((row, index) => ({
                ...row,
                position: index + 1,
            }));

            // Add the deleted items to the reordered list to keep them for the updated data
            const finalItems = [...reorderedNonDeletedItems, ...updatedRows.filter(row => row.deleted)];
    
            setRows(finalItems);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: finalItems }))
        } 
    }

    // Handle the showing of the end date field manually if needed
    const handleEndDateSuggestionClick = () => {
        setShowEndDateField(true);
    };

    return (
        <div className='rows-field'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing ? (
                // Edit mode
                <div className='edit-mode'>
                    {rows.length > 0 && (
                        <div className={`rows-header appointments-row ${showEndDateField ? 'show-end-date-field' : ''} ${showColorSelection ? 'show-colors-field' : ''}`}>
                            <div className='header-item'>{t('appointment.general.date_label')}</div>
                            <div className='header-item'>{t('appointment.general.start_time_label')}</div>
                            <div className='header-item'>{t('appointment.general.end_time_label')}</div>
                            <div className='header-item'>{t('appointment.general.break_label')}</div>
                            {showEndDateField && (
                                <div className='header-item'>{t('appointment.general.end_date_label')}</div>
                            )} 
                            <div className='header-item'>{t('appointment.general.hours_label')}</div>
                            <div className='header-item'>{t('appointment.general.assignees_label')}</div>
                            <div className='header-item'>{t('appointment.general.status_label')}</div>
                            {showColorSelection && (
                                <div className='header-item'>{t('appointment.general.custom_color_label')}</div>
                            )} 
                            <div className='delete-placeholder'></div>
                            <div className='delete-placeholder'></div>
                        </div>
                    )}
                    {rows && rows.filter(row => row.deleted === false).map((row) => {
                        const identifier = row.id?.toString() || row.uuid;
                        
                        return (
                            <div key={identifier} className='list-item'>
                                <div className={`appointments-row ${showEndDateField ? 'show-end-date-field' : ''} ${showColorSelection ? 'show-colors-field' : ''}`}>            
                                    <DateInput
                                        id={`start_date_${identifier}`}
                                        name={`start_date_${identifier}`}
                                        value={row.start_date ? new Date(row.start_date) : undefined}
                                        onChange={(selectedValue) => handleDateChange(selectedValue, 'start_date', identifier || '')}
                                        isInvalid={identifier && errorMessages?.appointments?.[identifier]?.['start_date'] ? true : false}
                                        placeholder='appointment.general.date_placeholder'
                                        hideDropdownIcon={true}
                                    />
                                    <TimeInput
                                        type='time'
                                        id={`start_time_${identifier}`}
                                        name={`start_time_${identifier}`}
                                        value={row.start_time ?? undefined}
                                        onChange={(selectedTime) => handleTimeChange(selectedTime, 'start_time', identifier || '')}
                                        isInvalid={identifier && errorMessages?.appointments?.[identifier]?.['start_time'] ? true : false}
                                        hideClearButton={true}
                                    />
                                    <TimeInput
                                        type='time'
                                        id={`end_time_${identifier}`}
                                        name={`end_time_${identifier}`}
                                        value={row.end_time ?? undefined}
                                        onChange={(selectedTime) => handleTimeChange(selectedTime, 'end_time', identifier || '')}
                                        isInvalid={identifier && errorMessages?.appointments?.[identifier]?.['end_time'] ? true : false}
                                        hideClearButton={true}
                                        baseTime={row.start_time}
                                    />
                                    <TimeInput
                                        type='time'
                                        id={`break_duration_${identifier}`}
                                        name={`break_duration_${identifier}`}
                                        timeRange={{ from: '00:00', to: '02:00' }}
                                        value={row.break_duration_string ?? undefined}
                                        onChange={(selectedTime) => handleBreakChange(selectedTime, identifier || '')}
                                        hideClearButton={true}
                                    />
                                    {showEndDateField && (
                                        // Show in case the end date is given but differs from the start date
                                        (row.end_date !== null && row.end_date !== row.start_date) || 
                                        // Show in case the end time is earlier than the start time, this probably means the user wants to set the timerow till tomorrow
                                        (row.end_time && row.start_time && row.end_time < row.start_time) ? (
                                            <DateInput
                                                id={`end_date_${identifier}`}
                                                name={`end_date_${identifier}`}
                                                value={row.end_date ? new Date(row.end_date) : undefined}
                                                onChange={(selectedValue) => handleDateChange(selectedValue, 'end_date', identifier || '')}
                                                placeholder='appointment.general.date_placeholder'
                                                hideDropdownIcon={true}
                                            />
                                        ) : (
                                            <div></div>
                                        )
                                    )}
                                    <div className='string-value'>
                                        {row.duration_string}
                                    </div>
                                    <div className='assignees-column'>
                                        <MultiSelect<AssigneeOptionType>
                                            options={assigneeOptions}
                                            id={`assignees_${identifier}`}
                                            name={`assignees_${identifier}`}
                                            disabled_selected={t('appointment.general.select_assignee_disabled_selected')}
                                            selectedOption={row.assignees || []}
                                            allowNoneOption={false}
                                            showSearch={true}
                                            selectionFormat={(option) => `${option.full_name}`}
                                            optionFormat={(option) => `${option.full_name}`}
                                            groupByField={assigneeGroupOptions ? 'option_group' : undefined}
                                            groupOptions={assigneeGroupOptions}
                                            groupTitleField='name'
                                            sortGroups={false}
                                            onChange={(selectedValue) => handleMultiSelectChange(selectedValue, 'assignees', identifier || '')}
                                            isInvalid={identifier && errorMessages?.appointments?.[identifier]?.['assignees'] ? true : false}
                                        />
                                        {workerPairsActive && (
                                            <ForemanSelect
                                                id={`foreman_${identifier}`}
                                                assignees={row.assignees || []}
                                                foremanId={row.foreman_id}
                                                onChange={(foremanId) => handleForemanChange(foremanId, identifier || '')}
                                            />
                                        )}
                                        {equipmentSchedulingActive && (
                                            // Equipment multiselect under the assignees
                                            <div className='equipment-select'>
                                                <MultiSelect<EquipmentType>
                                                    options={equipmentOptions}
                                                    id={`equipment_${identifier}`}
                                                    name={`equipment_${identifier}`}
                                                    disabled_selected={t('appointment.general.select_equipment_disabled_selected')}
                                                    selectedOption={row.equipment || []}
                                                    allowNoneOption={false}
                                                    showSearch={true}
                                                    selectionFormat={(option) => `${option.name}`}
                                                    optionFormat={(option) => `${option.name}`}
                                                    onChange={(selectedValue) => handleEquipmentChange(selectedValue, identifier || '')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <Dropdown<{ value: string, name: string }>
                                        options={appointmentStatuses.filter(status => status.value !== 'to_be_assigned')}
                                        id={`status_${identifier}`}
                                        name={`status_${identifier}`}
                                        disabled_selected={t('appointment.general.select_status_disabled_selected')}
                                        selectedOption={appointmentStatuses.find(status => status.value === row.status)}
                                        onChange={(selectedValue) => handleDropdownChange(selectedValue, 'status', identifier || '')}
                                        selectionFormat={(option) => `${option.name}`}
                                        optionFormat='name'
                                        allowNoneOption={false} 
                                        showSearch={false}
                                        disabled={row.status === 'to_be_assigned'}
                                    />
                                    {showColorSelection && (
                                        <Dropdown<{ value: string, name: string }>
                                            options={appointmentColors}
                                            id={`custom_color_${identifier}`}
                                            name={`custom_color_${identifier}`}
                                            disabled_selected={t('appointment.general.select_color_disabled_selected')}
                                            selectedOption={appointmentColors.find(status => status.value === row.custom_color)}
                                            onChange={(selectedValue) => handleDropdownChange(selectedValue, 'custom_color', identifier || '')}
                                            fixedDropdownWidth={100}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat='name'
                                            optionMode='dot'
                                            showSearch={false}
                                        />
                                    )}
                                    <div className={`appointment-locked-icon ${row.locked ? 'active' : ''} tooltip-icon`}
                                         onClick={() => handleLockToggle(identifier || '')}>
                                        {row.locked ? <FontAwesomeIcon icon={faLock} /> : <FontAwesomeIcon icon={faLockOpen} />}
                                        <span className="tooltip">{t('appointment.general.appointment_locked_tooltip')}</span>
                                    </div>
                                    <div className='delete-icon tooltip-icon'>
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            onClick={() => handleDeleteRow(identifier || '')} />
                                        <span className="tooltip">{t('general.delete')}</span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    {errorMessages && 
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'appointments', t, handleEndDateSuggestionClick)}
                        </div>
                    }
                    {(scheduleMultipleAppointments || rows.length === 0) && (
                        // Only allow to add new appointments based on the job appointment setting
                        <div onClick={(e) => {e.preventDefault(); handleAddRow(); }} 
                            className="add-new-button">
                                {t('appointment.general.add_appointment_label')}
                        </div>  
                    )}
                </div>
            ) : (
                // View mode
                rows.length > 0 && (
                    <div className={`full-width-alignment ${isEditable?.editability && !userHasOnlyViewRights ? 'editable' : ''}`}>
                        <div className="view-mode rows-view-mode">
                            <span className='p'>
                                <div className='item-list'>
                                    {rows.some(row => row.id) && (
                                        // Show the column labels above the rows
                                        <div className={`rows-header view-row view-appointments-row ${showEndDateField ? 'show-end-date-field' : ''} ${showColorSelection ? 'show-colors-field' : ''}`}>
                                            <div className='header-item'>{t('appointment.general.date_label')}</div>
                                            <div className='header-item'>{t('appointment.general.times_label')}</div>
                                            {showEndDateField && (
                                                <div className='header-item'>{t('appointment.general.end_date_label')}</div>
                                            )}
                                            <div className='header-item'>{t('appointment.general.assignees_label')}</div>
                                            <div className='header-item'>{t('appointment.general.status_label')}</div>
                                            {showColorSelection && (
                                                <div className='header-item'>{t('appointment.general.custom_color_label')}</div>
                                            )}
                                        </div>
                                    )}
                                    {rows.filter(row => row.id).map((row, index) => {
                                        // Map and combine the assignees
                                        const assignees = row.assignees?.map(assignee => assignee.full_name).join(', ');

                                        // Map and combine the equipment
                                        const equipment = row.equipment?.map(item => item.name).join(', ');

                                        // Format the start date field
                                        const startDate = row.start_date ? new Date(row.start_date).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';

                                        // Format the end date if it exists and differs from the start date
                                        const endDate = (row.end_date && row.end_date !== row.start_date)
                                            ? new Date(row.end_date).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' })
                                            : null;

                                        // Combine the start time, end time and duration into one value
                                        const times = row.start_time && row.end_time
                                            ? `${row.start_time}-${row.end_time}${row.duration_string ? ` (${row.duration_string})` : ''}`
                                            : '';

                                        return (
                                            <div key={index} className={`item view-row view-appointments-row ${showEndDateField ? 'show-end-date-field' : ''} ${showColorSelection ? 'show-colors-field' : ''}`}>
                                                <div>{startDate}</div>
                                                <div>
                                                    {times}
                                                    {row.break_duration ? <div className='row-remark'>{t('appointment.general.break_label')} {formatBreakDurationString(row.break_duration)}</div> : null}
                                                </div>
                                                {showEndDateField && (
                                                    <div>{endDate ?? ''}</div>
                                                )}
                                                <div>
                                                    {assignees}
                                                    {equipment && <div className='remark'>{equipment}</div>}
                                                </div>
                                                <div className='status-with-lock'>
                                                    {row.status && (
                                                        <div className='badge-container'>
                                                            <div className={`status-badge small appointment-${row.status}`}>
                                                                {t(`appointment.status.${row.status}`)}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {row.locked && (
                                                        <span className='appointment-locked-view-icon tooltip-icon'>
                                                            <FontAwesomeIcon icon={faLock} />
                                                            <span className='tooltip'>{t('appointment.general.appointment_locked_view_tooltip')}</span>
                                                        </span>
                                                    )}
                                                </div>
                                                {showColorSelection && (
                                                    <div>
                                                        {row.custom_color && (
                                                            <span className='color-dot' style={{ backgroundColor: `var(--${row.custom_color})` }}></span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {isEditable?.editability && !userHasOnlyViewRights && (
                                        // Only show add item label when the object is editable
                                        (scheduleMultipleAppointments || rows.length === 0) && (
                                            // Only allow to add new appointments based on the job appointment setting
                                            <div className='add-item'>
                                                {t('appointment.general.add_appointment_label')}
                                            </div>
                                            // TODO: Add in a later stage: add appointment by scheduling job modal
                                            // <div className='add-item' 
                                            //      onClick={(event) => handleOpenModal(event)}>
                                            //     {t('appointment.general.add_appointment_label')}
                                            // </div>
                                        )
                                    )}
                                </div>
                            </span>
                            <span className='edit-icon'>
                                <FontAwesomeIcon icon={faPen} />
                            </span> 
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

export default AppointmentsRowsField;